import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { WEAPON_TYPES, AMMO_TYPES, DEFENSIVE_SYSTEMS } from "../../../constants/ship";
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import {
  addModuleWeaponMount,
  removeModuleWeaponMount,
  updateModuleWeaponMount,
  addModuleDefensiveSystem,
  removeModuleDefensiveSystem,
} from "../../../store/features/moduleSlice";
import { Weapon, WeaponRange } from "../../../types/weapons";

// Get mount configuration based on type and turret type
const getMountConfig = (mountType: string, turretType?: string) => {
  switch (mountType) {
    case "smallWeaponMount":
      return { hardpointsUsed: 0, tons: 0.25, cost: 5000, maxWeapons: 1 }; // 0.25 tons per weapon, no hardpoints
    case "turret":
      const maxWeapons = turretType === "single" ? 1 : turretType === "double" ? 2 : 3;
      const turretCost = 
        turretType === "single" ? 200000 : // MCr0.2
        turretType === "double" ? 500000 : // MCr0.5
        1000000; // MCr1 for triple
      return { hardpointsUsed: 1, tons: 1, cost: turretCost, maxWeapons };
    case "barbette":
      return { hardpointsUsed: 1, tons: 5, cost: 0, maxWeapons: 1 };
    case "smallBay":
      return { hardpointsUsed: 1, tons: 50, cost: 0, maxWeapons: 1 };
    case "mediumBay":
      return { hardpointsUsed: 1, tons: 100, cost: 0, maxWeapons: 1 };
    case "largeBay":
      return { hardpointsUsed: 5, tons: 500, cost: 0, maxWeapons: 1 };
    case "spinalMount":
      return { hardpointsUsed: 0, tons: 0, cost: 0, maxWeapons: 1 };
    default:
      return { hardpointsUsed: 1, tons: 1, cost: 100000, maxWeapons: 1 };
  }
};

/**
 * Weapon Selector Component for Module Weapons
 */
const ModuleWeaponSelector: React.FC<{
  mountIndex: number;
  mount: any;
  removeMount?: () => void;
}> = ({ mountIndex, mount, removeMount }) => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const [selectedWeaponType, setSelectedWeaponType] = useState<string>("");
  const [selectedAmmoType, setSelectedAmmoType] = useState<string>("");
  const [selectedTurretType, setSelectedTurretType] = useState<string>("single");

  if (!moduleConfig) return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;

  // Get available weapons based on mount type and tech level
  const getAvailableWeapons = () => {
    return Object.entries(WEAPON_TYPES).filter(([id, weapon]) => {
      // Check tech level requirement
      if (weapon.techLevel > moduleConfig.techLevel) {
        return false;
      }

      // Check mount type compatibility
      switch (mount.type) {
        case "turret":
          // Turrets can mount turret weapons (including sandcaster)
          return weapon.mountType === "turret";
        case "barbette":
          // Barbettes can mount barbette weapons
          return weapon.mountType === "barbette";
        case "smallBay":
          // Small bays can mount small bay weapons
          return weapon.mountType === "smallBay";
        case "mediumBay":
          // Medium bays can mount medium bay weapons
          return weapon.mountType === "mediumBay";
        case "largeBay":
          // Large bays can mount large bay weapons
          return weapon.mountType === "largeBay";
        case "spinalMount":
          // Spinal mounts can mount spinal weapons
          return weapon.mountType === "spinalMount";
        case "pointDefense":
          // Point defense mounts are for point defense weapons only (exclude sandcaster)
          return weapon.traits.includes("Point Defense") && weapon.id !== "sandcaster";
        case "fixed":
          // Fixed mounts can mount turret weapons (same as turrets)
          return weapon.mountType === "turret";
        default:
          return true;
      }
    });
  };

  const availableWeapons = getAvailableWeapons();

  // Check if mount is at weapon capacity
  const isAtCapacity = () => {
    const mountConfig = getMountConfig(mount.type, selectedTurretType);
    return (mount.weapons?.length || 0) >= mountConfig.maxWeapons;
  };

  // Check if selected weapon requires ammunition
  const selectedWeaponRequiresAmmo = () => {
    if (!selectedWeaponType) return false;
    const weaponData = WEAPON_TYPES[selectedWeaponType as keyof typeof WEAPON_TYPES];
    return weaponData && (weaponData as any).ammo;
  };

  const handleWeaponSelect = () => {
    if (!selectedWeaponType || isAtCapacity()) return;

    const weaponData = WEAPON_TYPES[selectedWeaponType as keyof typeof WEAPON_TYPES];
    if (!weaponData) return;

    // If weapon uses ammo, get the selected ammo type
    let ammoType = undefined;
    let ammoInfo = undefined;
    if ((weaponData as any).ammo) {
      ammoType = selectedAmmoType;
      ammoInfo = AMMO_TYPES[ammoType as keyof typeof AMMO_TYPES];
      if (!ammoInfo) return; // Must select an ammo type
    }

    // Convert to Weapon interface format
    const newWeapon: Weapon = {
      name: weaponData.name,
      description: weaponData.description,
      tl: weaponData.techLevel,
      range: weaponData.range as WeaponRange,
      power: weaponData.power,
      damage: weaponData.damage,
      cost: weaponData.cost * 1000000, // Convert MCr to Cr
      tons: weaponData.weight,
      traits: weaponData.traits,
      mountType: weaponData.mountType,
      damageMultiple: (weaponData as any).damageMultiple,
      attackModifier: (weaponData as any).attackModifier,
      baseSize: (weaponData as any).baseSize,
      maxSize: (weaponData as any).maxSize,
      ammo: ammoInfo
        ? {
            type: ammoInfo.type,
            capacity: (weaponData as any).ammo.capacity,
            tonsPerReload: ammoInfo.tonsPerReload,
            costPerReload: ammoInfo.cost,
            name: ammoInfo.name,
            traits: ammoInfo.traits,
            damage: ammoInfo.damage,
            perReload: ammoInfo.perReload,
            tl: ammoInfo.tl,
          }
        : (weaponData as any).ammo,
      currentAmmo: ammoInfo ? (weaponData as any).ammo.capacity : undefined,
      selectedAmmoType: ammoType,
    };

    // Get mount configuration
    const mountConfig = getMountConfig(mount.type, selectedTurretType);

    // Update mount with new weapon
    const updatedMount = {
      ...mount,
      turretType: mount.type === "turret" ? selectedTurretType : mount.turretType,
      weapons: [...(mount.weapons || []), newWeapon],
      cost: mountConfig.cost + (mount.cost || 0) + newWeapon.cost,
      tons: mountConfig.tons + (mount.tons || 0) + newWeapon.tons,
      power: (mount.power || 0) + newWeapon.power,
      hardpointsUsed: mountConfig.hardpointsUsed,
      maxWeapons: mountConfig.maxWeapons,
    };

    dispatch(updateModuleWeaponMount({ index: mountIndex, mount: updatedMount }));
    setSelectedWeaponType("");
    setSelectedAmmoType("");
  };

  const removeWeapon = (weaponIndex: number) => {
    const weaponToRemove = mount.weapons[weaponIndex];
    const mountConfig = getMountConfig(mount.type, mount.turretType);
    
    const updatedMount = {
      ...mount,
      weapons: mount.weapons.filter((_: any, index: number) => index !== weaponIndex),
      cost: mountConfig.cost + (mount.cost || 0) - weaponToRemove.cost,
      tons: mountConfig.tons + (mount.tons || 0) - weaponToRemove.tons,
      power: (mount.power || 0) - weaponToRemove.power,
    };
    
    dispatch(updateModuleWeaponMount({ index: mountIndex, mount: updatedMount }));
  };

  const getMountTypeDisplay = () => {
    const typeMap: Record<string, string> = {
      turret: "Turret",
      barbette: "Barbette",
      smallBay: "Small Bay",
      mediumBay: "Medium Bay",
      largeBay: "Large Bay",
      spinalMount: "Spinal Mount",
      pointDefense: "Point Defense",
      fixed: "Fixed",
    };
    return typeMap[mount.type] || mount.type;
  };

  const getTurretTypeDisplay = () => {
    if (mount.type !== "turret") return "";
    const turretType = mount.turretType || selectedTurretType;
    const typeMap: Record<string, string> = {
      single: "Single",
      double: "Double",
      triple: "Triple",
    };
    return ` (${typeMap[turretType] || turretType})`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {getMountTypeDisplay()}{getTurretTypeDisplay()} Mount
        </h3>
        {removeMount && (
          <button
            className="px-3 py-1 text-sm border border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
            onClick={removeMount}
          >
            Remove Mount
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mount Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Cost:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(mount.cost || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Weight:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(mount.tons || 0)} tons</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Power:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(mount.power || 0)} Power</span>
            </div>
            {mount.hardpointsUsed && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Hardpoints:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{mount.hardpointsUsed}</span>
              </div>
            )}
            {mount.maxWeapons && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Weapon Capacity:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{(mount.weapons?.length || 0)}/{mount.maxWeapons}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add Weapon</h4>
          {!isAtCapacity() ? (
            <div className="space-y-3">
              {/* Turret Type Selection for Turret Mounts */}
              {mount.type === "turret" && (
                <select
                  value={selectedTurretType}
                  onChange={(e) => setSelectedTurretType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="single">Single Turret (1 weapon)</option>
                  <option value="double">Double Turret (2 weapons)</option>
                  <option value="triple">Triple Turret (3 weapons)</option>
                </select>
              )}

              <select
                value={selectedWeaponType}
                onChange={(e) => setSelectedWeaponType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Select Weapon</option>
                {availableWeapons.map(([id, weapon]) => (
                  <option key={id} value={id}>
                    {weapon.name} - {weapon.damage} damage, {weapon.range} range
                  </option>
                ))}
              </select>

              {/* Only show ammunition selection if the selected weapon actually requires it */}
              {selectedWeaponType && selectedWeaponRequiresAmmo() && (
                <select
                  value={selectedAmmoType}
                  onChange={(e) => setSelectedAmmoType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Ammunition</option>
                  {Object.entries(AMMO_TYPES).map(([id, ammo]) => (
                    <option key={id} value={id}>
                      {ammo.name} - {ammo.damage} damage
                    </option>
                  ))}
                </select>
              )}

              <button
                onClick={handleWeaponSelect}
                disabled={!selectedWeaponType || (selectedWeaponRequiresAmmo() && !selectedAmmoType)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Add Weapon
              </button>
            </div>
          ) : (
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
              Mount is at capacity
            </div>
          )}
        </div>
      </div>

      {/* Installed Weapons */}
      {mount.weapons && mount.weapons.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Installed Weapons</h4>
          <div className="space-y-2">
            {mount.weapons.map((weapon: any, weaponIndex: number) => (
              <div key={weaponIndex} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{weapon.name}</div>
                  <div className="text-sm text-gray-600">
                    {weapon.damage} damage, {weapon.range} range
                    {weapon.ammo && `, ${weapon.ammo.name} ammo`}
                  </div>
                </div>
                <button
                  onClick={() => removeWeapon(weaponIndex)}
                  className="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Module Weapons Step Component
 * Handles weapons and defensive system selection for modules
 */
export const ModuleWeaponsStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const [activeTab, setActiveTab] = useState<"weapons" | "defensive" | "summary">("weapons");

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  const weaponMounts = moduleConfig.systems.weaponMounts || [];
  const defensiveSystems = moduleConfig.systems.defensiveSystems || [];

  // Calculate hardpoints based on module size (1 per 100 tons)
  const availableHardpoints = Math.floor(moduleConfig.size / 100);
  const usedHardpoints = weaponMounts.reduce((total, mount) => total + (mount.hardpointsUsed || 0), 0);
  const remainingHardpoints = availableHardpoints - usedHardpoints;

  // Calculate total weapon stats
  const totalWeaponCost = weaponMounts.reduce((total, mount) => total + (mount.cost || 0), 0);
  const totalWeaponWeight = weaponMounts.reduce((total, mount) => total + (mount.tons || 0), 0);
  const totalWeaponPower = weaponMounts.reduce((total, mount) => total + (mount.power || 0), 0);

  const tabs = [
    { id: "weapons", label: "Weapons", icon: "⚡" },
    { id: "defensive", label: "Defensive Systems", icon: "🛡️" },
    { id: "summary", label: "Summary", icon: "📊" },
  ] as const;

  const handleAddWeaponMount = (mountType: string) => {
    const mountConfig = getMountConfig(mountType, "single");
    const newMount = {
      type: mountType,
      turretType: mountType === "turret" ? "single" : undefined,
      weapons: [],
      cost: mountConfig.cost,
      tons: mountConfig.tons,
      power: 0,
      hardpointsUsed: mountConfig.hardpointsUsed,
      maxWeapons: mountConfig.maxWeapons,
    };
    dispatch(addModuleWeaponMount(newMount));
  };

  const handleRemoveWeaponMount = (index: number) => {
    dispatch(removeModuleWeaponMount(index));
  };

  const handleAddDefensiveSystem = (systemId: string) => {
    const systemData = DEFENSIVE_SYSTEMS[systemId as keyof typeof DEFENSIVE_SYSTEMS];
    if (!systemData) return;

    const newSystem = {
      id: systemData.id,
      name: systemData.name,
      description: systemData.description,
      cost: systemData.cost * 1000000, // Convert MCr to Cr
      weight: systemData.weight,
      power: systemData.power,
      crew: systemData.crew,
      techLevel: systemData.techLevel,
      traits: systemData.traits,
      effect: systemData.effect,
      quantity: 1, // Default quantity for module systems
      ...("flickerRates" in systemData
        ? { flickerRates: (systemData as any).flickerRates }
        : {}),
      ...("capacitorAbsorption" in systemData
        ? { capacitorAbsorption: (systemData as any).capacitorAbsorption }
        : {}),
      ...("capacitorCost" in systemData
        ? { capacitorCost: (systemData as any).capacitorCost }
        : {}),
    };
    dispatch(addModuleDefensiveSystem(newSystem));
  };

  const handleRemoveDefensiveSystem = (index: number) => {
    dispatch(removeModuleDefensiveSystem(index));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Module Weapons & Defensive Systems Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Select weapons and defensive systems for your module
        </p>
      </div>

      {/* Module Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Module Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.size)} tons</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Tech Level:</span>
            <div className="text-blue-900 dark:text-blue-100">TL {moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Available Hardpoints:</span>
            <div className="text-blue-900 dark:text-blue-100">{availableHardpoints} ({remainingHardpoints} remaining)</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Remaining Tons:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.calculations.remainingTons)} tons</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-600">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "weapons" && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Weapon Mounts</h3>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Hardpoints: {usedHardpoints}/{availableHardpoints}
              </div>
            </div>

            {/* Add Weapon Mount */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Add Weapon Mount</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {["turret", "barbette", "smallBay", "mediumBay", "largeBay", "spinalMount", "pointDefense"].map((mountType) => (
                  <button
                    key={mountType}
                    onClick={() => handleAddWeaponMount(mountType)}
                    disabled={remainingHardpoints <= 0}
                    className={`px-3 py-2 text-sm rounded transition-colors ${
                      remainingHardpoints > 0
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    }`}
                    title={remainingHardpoints <= 0 ? "No hardpoints remaining" : `Add ${mountType} mount`}
                  >
                    {mountType.charAt(0).toUpperCase() + mountType.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Weapon Mounts List */}
            {weaponMounts.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No weapon mounts configured. Add a weapon mount to get started.
              </div>
            ) : (
              <div className="space-y-4">
                {weaponMounts.map((mount, index) => (
                  <ModuleWeaponSelector
                    key={index}
                    mountIndex={index}
                    mount={mount}
                    removeMount={() => handleRemoveWeaponMount(index)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "defensive" && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Defensive Systems</h3>

            {/* Add Defensive System */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Add Defensive System</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(DEFENSIVE_SYSTEMS).map(([id, system]) => (
                <button
                  key={id}
                  onClick={() => handleAddDefensiveSystem(id)}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  {system.name}
                </button>
              ))}
            </div>
            </div>

            {/* Defensive Systems List */}
            {defensiveSystems.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No defensive systems configured. Add a defensive system to get started.
              </div>
            ) : (
              <div className="space-y-4">
                {defensiveSystems.map((system, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-gray-800 dark:text-gray-100">
                        {system.name}
                        {(system.quantity || 1) > 1 && (
                          <span className="ml-2 text-sm font-normal text-gray-600 dark:text-gray-400">
                            (×{system.quantity})
                          </span>
                        )}
                      </h4>
                      <button
                        onClick={() => handleRemoveDefensiveSystem(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      {system.description}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Cost:</span> <span className="text-gray-900 dark:text-gray-100">{formatCurrency((system.cost || 0) * (system.quantity || 1))}</span>
                        {(system.quantity || 1) > 1 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            ({formatCurrency(system.cost || 0)} each)
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Weight:</span> <span className="text-gray-900 dark:text-gray-100">{formatNumber((system.weight || 0) * (system.quantity || 1))} tons</span>
                        {(system.quantity || 1) > 1 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            ({system.weight || 0} tons each)
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Power:</span> <span className="text-gray-900 dark:text-gray-100">{formatNumber((system.power || 0) * (system.quantity || 1))} Power</span>
                        {(system.quantity || 1) > 1 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            ({system.power || 0} each)
                          </span>
                        )}
                      </div>
                    </div>
                    {system.effect && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Effect:</span> <span className="text-gray-900 dark:text-gray-100">{system.effect}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "summary" && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Weapons Summary</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Total Cost</h4>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalWeaponCost)}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">Total Weight</h4>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(totalWeaponWeight)} tons</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Total Power</h4>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatNumber(totalWeaponPower)} Power</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-purple-800 dark:text-purple-200">Hardpoints Used</h4>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{usedHardpoints}/{availableHardpoints}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-2">Weapon Mounts ({weaponMounts.length})</h4>
                {weaponMounts.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No weapon mounts configured</p>
                ) : (
                  <div className="space-y-2">
                    {weaponMounts.map((mount, index) => (
                      <div key={index} className="text-sm bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        <span className="text-gray-900 dark:text-gray-100">{mount.type.charAt(0).toUpperCase() + mount.type.slice(1)} Mount - 
                        Cost: {formatCurrency(mount.cost || 0)}, 
                        Weight: {formatNumber(mount.tons || 0)} tons, 
                        Power: {formatNumber(mount.power || 0)} Power</span>
                        {mount.weapons && mount.weapons.length > 0 && (
                          <span className="ml-2 text-gray-600 dark:text-gray-400">
                            ({mount.weapons.length} weapons)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-2">Defensive Systems ({defensiveSystems.length})</h4>
                {defensiveSystems.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No defensive systems configured</p>
                ) : (
                  <div className="space-y-2">
                    {defensiveSystems.map((system, index) => (
                      <div key={index} className="text-sm bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        <span className="text-gray-900 dark:text-gray-100">{system.name} - 
                        Cost: {formatCurrency(system.cost || 0)}, 
                        Weight: {formatNumber(system.weight || 0)} tons, 
                        Power: {formatNumber(system.power || 0)} Power</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer with Weapon Stats */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
        <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
          <div>
            <span className="text-gray-400 block text-sm">Cost</span>
            <span className="text-xl font-bold text-green-400">
              {formatCurrency(totalWeaponCost)}
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Weight</span>
            <span className="text-xl font-bold text-blue-400">
              {formatNumber(totalWeaponWeight)} tons
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Power</span>
            <span className="text-xl font-bold text-yellow-400">
              {formatNumber(totalWeaponPower)} Power
            </span>
          </div>
          <div>
            <span className="text-gray-400 block text-sm">Hardpoints</span>
            <span className="text-xl font-bold text-purple-400">
              {usedHardpoints}/{availableHardpoints}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
